import { defineStore } from "pinia";
import { AirlineOrderGrouping } from "./entity";
import { AirlineOrderGroupingList } from "./list.gen";
import type {
  AirlineOrderGroupingStore,
  AirlineOrderGroupingStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const airlineOrderGroupingList = new AirlineOrderGroupingList();
export const useAirlineOrderGroupingStore = defineStore(
  "airlineOrderGroupingStore",
  {
    state: (): AirlineOrderGroupingStoreState => ({
      activeID: null,
      cache: {},
      list: [] as AirlineOrderGrouping[],
      retrievalTimestamp: 0,
      updateKey: 0,
      storeUpdateTimeout: null,
    }),
    actions: {
      initializeStore(): void {
        this.convertList();
      },
      emitFormUpdateEvent(id: string): void {
        window.dispatchEvent(
          new CustomEvent("form-update-AirlineOrderGrouping", {
            detail: { id },
          }),
        );
      },
      emitStoreUpdateEvent(): void {
        // Clear existing timeout
        if (this.storeUpdateTimeout !== null) {
          clearTimeout(this.storeUpdateTimeout);
        }

        // Set new timeout
        this.storeUpdateTimeout = setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("store.update.AirlineOrderGrouping"),
          );
          this.storeUpdateTimeout = null;
        }, 1500);
      },
      async activate(
        airlineOrderGrouping: AirlineOrderGrouping,
      ): Promise<void> {
        // Set active
        await airlineOrderGrouping
          .activate(auth.firebaseUser?.uid)
          .then(() => {
            this.activeID = airlineOrderGrouping.id;
          })
          .catch((error) => {
            console.error(error);
            this.activeID = null;
          });
      },
      add(airlineOrderGrouping: AirlineOrderGrouping): void {
        const id = airlineOrderGrouping.id;
        if (typeof id === "string" && !this.contains(id)) {
          this.list.push(airlineOrderGrouping);

          // Set active
          if (this.active === null) {
            this.activeID = airlineOrderGrouping.id;
          }
        } else {
          this.reinsert(airlineOrderGrouping);
        }
        this.updateKey += 1;
      },
      convertList(): void {
        this.list = (this.list as AirlineOrderGrouping[]).map((x: any) => {
          const airlineOrderGrouping = new AirlineOrderGrouping({});
          airlineOrderGrouping.set(x as JSONData);
          return airlineOrderGrouping;
        });
      },
      async delete(airlineOrderGrouping: AirlineOrderGrouping): Promise<void> {
        await airlineOrderGrouping
          .delete()
          .then(() => {
            this.remove(airlineOrderGrouping);
          })
          .finally(() => {
            this.emitStoreUpdateEvent();
          });
      },
      async deleteById(id: string): Promise<void> {
        const airlineOrderGrouping = (this.list as AirlineOrderGrouping[]).find(
          (x: AirlineOrderGrouping) => x.id === id,
        );
        if (airlineOrderGrouping !== undefined) {
          await this.delete(airlineOrderGrouping);
        } else {
          throw new Error("Could not find AirlineOrderGrouping with id: " + id);
        }
      },
      contains(id: string): boolean {
        return (this.list as AirlineOrderGrouping[]).some(
          (x: AirlineOrderGrouping) => x.id === id,
        );
      },
      get(id: string): AirlineOrderGrouping | null {
        return (
          (this.list as AirlineOrderGrouping[]).find(
            (x: AirlineOrderGrouping) => x.id === id,
          ) || null
        );
      },
      reinsert(airlineOrderGrouping: AirlineOrderGrouping): void {
        const index = (this.list as AirlineOrderGrouping[]).findIndex(
          (x: AirlineOrderGrouping) => x.id === airlineOrderGrouping.id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            airlineOrderGrouping,
            ...this.list.slice(index + 1),
          ];
        } else {
          this.add(airlineOrderGrouping);
        }
        this.updateKey += 1;
      },
      remove({ id }: AirlineOrderGrouping): void {
        const index = (this.list as AirlineOrderGrouping[]).findIndex(
          (x: AirlineOrderGrouping) => x.id === id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            ...this.list.slice(index + 1),
          ];
        }
        this.updateKey += 1;
      },
      async retrieve(id: string, force = false): Promise<AirlineOrderGrouping> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          const airlineOrderGrouping = this.get(id);
          if (airlineOrderGrouping !== null) {
            return airlineOrderGrouping;
          }
        }

        // Retrieve airlineOrderGrouping
        const airlineOrderGrouping = new AirlineOrderGrouping({});

        // Get airlineOrderGrouping
        await airlineOrderGrouping.get([id]).then(async (entity) => {
          this.reinsert(entity);

          // Activate airlineOrderGrouping
          await this.activate(entity);
        });

        return airlineOrderGrouping;
      },
      async retrieveAll(force = false): Promise<{
        active: string | null;
        items: AirlineOrderGrouping[];
      }> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          return {
            active: this.activeID,
            items: this.list as AirlineOrderGrouping[],
          };
        }

        // Clear list
        while (this.list.length > 0) {
          this.list.pop();
        }

        // Retrieve list
        await airlineOrderGroupingList.getAll().then(async (list) => {
          list.forEach((airlineOrderGrouping) => {
            this.reinsert(airlineOrderGrouping);
          });

          // Activate airlineOrderGrouping
          if (list.length > 0) {
            if (airlineOrderGroupingList.active !== null) {
              this.activeID = airlineOrderGroupingList.active;
            } else {
              await this.activate(list[0]);
            }
          }
        });

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        // Set user property
        if (this.activeID !== null) {
          analytics.setUserProperty("airline_order_grouping_id", this.activeID);
        }

        return {
          active: this.activeID,
          items: this.list as AirlineOrderGrouping[],
        };
      },
      async retrievePaginated(
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: AirlineOrderGrouping[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as AirlineOrderGrouping,
            ),
          };
        }

        // Retrieve list
        const items = await airlineOrderGroupingList
          .getPaginated([], pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((airlineOrderGrouping) => {
              this.reinsert(airlineOrderGrouping);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: AirlineOrderGrouping) => x.id),
          pagination: newPagination,
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items,
        };
      },
      async query(
        queries: Query[],
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: AirlineOrderGrouping[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as AirlineOrderGrouping,
            ),
          };
        }

        // Retrieve list
        const items = await airlineOrderGroupingList
          .query(queries, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((airlineOrderGrouping) => {
              this.reinsert(airlineOrderGrouping);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: AirlineOrderGrouping) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items: items,
        };
      },
      async search(
        query: string,
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: AirlineOrderGrouping[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey =
          query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            items: cached.ids.map(
              (id: string) => this.get(id) as AirlineOrderGrouping,
            ),
            pagination: cached.pagination.deepCopy(),
          };
        }

        // Retrieve list
        const items = await airlineOrderGroupingList
          .search(query, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((airlineOrderGrouping) => {
              this.reinsert(airlineOrderGrouping);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: AirlineOrderGrouping) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          items: items,
          pagination: newPagination,
        };
      },
      async update(
        airlineOrderGrouping: AirlineOrderGrouping,
      ): Promise<string> {
        // Update airlineOrderGrouping
        await airlineOrderGrouping.update();

        // Reinsert airlineOrderGrouping
        this.reinsert(airlineOrderGrouping);

        // Activate airlineOrderGrouping
        if (this.active === null) {
          await this.activate(airlineOrderGrouping);
        }

        this.emitStoreUpdateEvent();

        return airlineOrderGrouping.id;
      },
    },
    getters: {
      active(): AirlineOrderGrouping | null {
        if (this.activeID === null) {
          return null;
        }

        const airlineOrderGrouping = (this.list as AirlineOrderGrouping[]).find(
          (x: AirlineOrderGrouping) => x.id === this.activeID,
        );
        if (airlineOrderGrouping === undefined) {
          return null;
        }

        return airlineOrderGrouping;
      },
      refreshRequired(): boolean {
        return (
          this.retrievalTimestamp === 0 ||
          this.retrievalTimestamp < Date.now() - 30 * 60000 ||
          this.list.length === 0
        );
      },
    },
  },
);

// Export a function to create a new instance of the store
export function createAirlineOrderGroupingStore(): AirlineOrderGroupingStore {
  const store = useAirlineOrderGroupingStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as AirlineOrderGroupingStore; // TODO: Fix this
}
